import type { ReactNode } from 'react';
import { merge } from 'lodash-es';

import type { SpacingValue } from '../../../common/sizing';
import { colors, darkThemeSelector, styled } from '../../../stitches.config';
import { Small } from '../../../text/Small';
import { heightCSS } from '../../../utilities/shared/Height';
import { marginCSS } from '../../../utilities/shared/Margin';
import { maxHeightCSS } from '../../../utilities/shared/MaxHeight';
import { maxWidthCSS } from '../../../utilities/shared/MaxWidth';
import { minHeightCSS } from '../../../utilities/shared/MinHeight';
import { minWidthCSS } from '../../../utilities/shared/MinWidth';
import { widthCSS } from '../../../utilities/shared/Width';
import { type HopSharedProps, hopsVars } from './utils';

export type HopSpanProps = HopSharedProps & {
  type: 'span';
  children?: ReactNode;
  visibility?: 'visible' | 'hidden';
  /**
   * Inset amount for `objectAdjacentPadding` sides. Defaults to `hopsVars.spanObjectAdjacentPadding`.
   * Set on each `type: 'span'` entry in `<Hops hops={…} />` when building the list.
   */
  buffer?: SpacingValue;
  /**
   * Which sides (relative to layout `direction`) get extra inset beside adjacent `object` hops.
   * Usually set by `<Hops>` from neighbor detection.
   */
  bufferEnd?: 'start' | 'end' | 'both';
};

const HopSpanVisual = styled(Small, {
  position: 'absolute',
  top: '50%',
  left: 0,
  display: 'flex',
  alignItems: 'center',
  padding: '$8 $2',
  background: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,
  borderRadius: '$8',
  color: colors.bodyNeutralLight,
  transform: 'rotate(180deg) translate(50%, 50%)',
  writingMode: 'vertical-rl',
  fontWeight: '$bold',
  whiteSpace: 'nowrap',

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
    color: colors.bodyNeutralDark,
  },
});
HopSpanVisual.displayName = 'HopSpanVisual';

const HopSpanContainer = styled(Small, {
  position: 'relative',
  zIndex: hopsVars.zIndex.span,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    direction: {
      row: {
        flexDirection: 'column',
        widthAll: 'min-content',
      },
      column: {
        flexDirection: 'row',
        heightAll: 'min-content',
      },
    },
    size: {
      small: {},
      large: {},
    },
    visibility: {
      visible: {},
      hidden: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
});
HopSpanContainer.displayName = 'HopSpanContainer';

function hopSpanBufferCSS(
  direction: 'column' | 'row',
  sides: 'both' | 'end' | 'start',
  buffer: SpacingValue,
): ReturnType<typeof marginCSS> {
  if (direction === 'row') {
    return marginCSS({
      left: sides === 'start' || sides === 'both' ? buffer : undefined,
      right: sides === 'end' || sides === 'both' ? buffer : undefined,
    });
  }
  return marginCSS({
    top: sides === 'start' || sides === 'both' ? buffer : undefined,
    bottom: sides === 'end' || sides === 'both' ? buffer : undefined,
  });
}

export function HopSpan({
  type: _hopSpanKind,
  buffer,
  bufferEnd,
  id,
  direction = 'row',
  size = 'small',
  visibility = 'visible',
  children,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
  ...remaining
}: HopSpanProps) {
  const padAmount = buffer ?? hopsVars.span.buffer;
  const neighborPaddingCss =
    bufferEnd != null ? hopSpanBufferCSS(direction, bufferEnd, padAmount) : {};

  const css = merge(
    neighborPaddingCss,
    visibility === 'hidden'
      ? merge(
          widthCSS(width),
          maxWidthCSS(maxWidth),
          minWidthCSS(minWidth),
          heightCSS(height),
          maxHeightCSS(maxHeight),
          minHeightCSS(minHeight),
        )
      : {},
  );

  return (
    <HopSpanContainer
      id={id}
      direction={direction}
      size={size}
      visibility={visibility}
      css={css}
      {...remaining}
    >
      <HopSpanVisual> {children} </HopSpanVisual>
    </HopSpanContainer>
  );
}
